import type React from "react";
import { useEffect, useRef, useState } from "react";
import { ChevronDownIcon } from "~/components/icons/ChevronDownIcon";
import { ChevronUpIcon } from "~/components/icons/ChevronUpIcon";

export interface SelectOption {
	name: string | React.ReactNode;
	value: string;
	group?: string;
	disabled?: boolean;
}

interface SelectProps {
	name: string;
	label?: string;
	options: SelectOption[];
	selected?: SelectOption | SelectOption[] | null;
	onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
	required?: boolean;
	invalid?: boolean;
	disabled?: boolean;
	multiple?: boolean;
	display?: "stacked" | "inline";
	theme?: Theme;
	/** Content shown at the bottom of the open dropdown. */
	footer?: React.ReactNode;
}

export const Select = ({
	name,
	label,
	options,
	selected = null,
	onChange,
	required = false,
	invalid = false,
	disabled = false,
	multiple = false,
	display = "stacked",
	theme = "light",
	footer,
}: SelectProps) => {
	const initialSelection = Array.isArray(selected)
		? selected.map((option) => option.value)
		: selected?.value
			? [selected.value]
			: [];
	const [selectedValues, setSelectedValues] =
		useState<string[]>(initialSelection);
	const [open, setOpen] = useState(false);
	const rootRef = useRef<HTMLDivElement>(null);
	const selectRef = useRef<HTMLSelectElement>(null);

	useEffect(() => {
		const handlePointerDown = (event: MouseEvent) => {
			if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
		};
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") setOpen(false);
		};
		document.addEventListener("mousedown", handlePointerDown);
		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.removeEventListener("mousedown", handlePointerDown);
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, []);

	const selectedOptions = options.filter((option) =>
		selectedValues.includes(option.value),
	);
	const selectedLabel = selectedOptions.length
		? multiple
			? `${selectedOptions.length} selected`
			: selectedOptions[0].name
		: `Select one${multiple ? " or more" : ""}...`;

	const notifyChange = (values: string[]) => {
		setSelectedValues(values);
		if (selectRef.current && onChange) {
			selectRef.current.value = multiple ? "" : (values[0] ?? "");
			onChange({
				target: selectRef.current,
				currentTarget: selectRef.current,
			} as React.ChangeEvent<HTMLSelectElement>);
		}
	};

	const chooseOption = (option: SelectOption) => {
		if (option.disabled) return;
		if (multiple) {
			const values = selectedValues.includes(option.value)
				? selectedValues.filter((value) => value !== option.value)
				: [...selectedValues, option.value];
			notifyChange(values);
		} else {
			notifyChange([option.value]);
			setOpen(false);
		}
	};

	return (
		<div
			ref={rootRef}
			className={`flex flex-col gap-2 ${
				display === "inline" ? "sm:flex-row sm:items-center" : ""
			}`}
		>
			{label && (
				<label
					htmlFor={name}
					className={`text-secondary-900 ${display === "inline" ? "sm:w-1/3" : ""}`}
				>
					{label}
					{required ? (
						""
					) : (
						<span className="ml-1 text-secondary-500">(optional)</span>
					)}
				</label>
			)}

			<div
				className={`relative ${display === "inline" ? "sm:w-2/3" : "w-full"}`}
			>
				<button
					type="button"
					id={name}
					aria-haspopup="listbox"
					aria-expanded={open}
					disabled={disabled}
					onClick={() => setOpen((isOpen) => !isOpen)}
					className={`flex min-h-12 w-full items-center justify-between rounded border p-3 text-left ${theme === "light" ? "bg-transparent" : "bg-secondary-dark-input-bg"} ${
						invalid
							? "border-primary ring-4 ring-primary-200"
							: disabled
								? `cursor-not-allowed text-gray-400 ${theme === "light" ? "border-secondary-200" : "border-secondary-700"}`
								: theme === "light"
									? "border-secondary-200 text-gray-900"
									: "border-secondary-700 text-white"
					}`}
				>
					<span
						className={
							disabled
								? `${theme === "light" ? "text-secondary-200" : "text-secondary-400"}`
								: `${theme === "light" ? "text-gray-900" : "text-white"}`
						}
					>
						{selectedLabel}
					</span>
					<span aria-hidden="true" className="ml-3 shrink-0">
						{open ? <ChevronUpIcon /> : <ChevronDownIcon />}
					</span>
				</button>

				{open && (
					// biome-ignore lint/a11y/useSemanticElements: this custom listbox needs a scrollable popover.
					<div
						role="listbox"
						aria-label={label ?? name}
						aria-multiselectable={multiple}
						tabIndex={0}
						className="absolute z-20 mt-2 flex max-h-96 w-full flex-col overflow-hidden rounded border border-secondary-200 bg-white shadow-lg"
					>
						<div className="max-h-72 overflow-y-auto p-2">
							{options.map((option) => {
								const isSelected = selectedValues.includes(option.value);
								return (
									<button
										key={option.value}
										type="button"
										role="option"
										aria-selected={isSelected}
										disabled={option.disabled}
										onClick={() => chooseOption(option)}
										className="flex w-full items-center gap-4 rounded-lg px-3 py-3 text-left text-base text-gray-900 hover:bg-secondary-50 disabled:cursor-not-allowed disabled:opacity-50"
									>
										<span
											aria-hidden="true"
											className={`h-5 w-5 shrink-0 rounded-full border-2 ${isSelected ? "border-primary p-1.5 bg-primary bg-clip-content" : "border-secondary-200"}`}
										/>
										<span>{option.name}</span>
									</button>
								);
							})}
						</div>
						{footer && (
							<div className="border-t border-secondary-200 flex p-2 justify-center items-center">
								{footer}
							</div>
						)}
					</div>
				)}

				<select
					ref={selectRef}
					className="pointer-events-none absolute h-px w-px opacity-0"
					id={`${name}-native`}
					name={name}
					aria-hidden="true"
					tabIndex={-1}
					disabled={disabled}
					multiple={multiple}
					value={multiple ? selectedValues : (selectedValues[0] ?? "")}
					onChange={() => undefined}
				>
					{options.map((option) => (
						<option
							key={option.value}
							value={option.value}
							disabled={option.disabled}
						>
							{option.name}
						</option>
					))}
				</select>
			</div>
		</div>
	);
};
